import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Loader2, ZoomIn, X, ChevronLeft, ChevronRight, FileText } from 'lucide-react';

/**
 * İşlenen belgenin görseli — form yanında TEYİT için (kullanıcı, 2026-10-03:
 * "mal kabul sayfasında oraya geldiğimiz görseli göremiyoruz, işlemin
 * doğruluğunu teyit edebilelim").
 *
 * `sayfalar`: [{ sira, content_type }], `sayfaAdresi(sira)`: API yolu.
 * Sayfalar JWT gerektirdiği için `<img src>` ile doğrudan çekilemez; blob
 * alınıp object URL üretilir. Büyütme sayfanın İÇİNDE katmandır — ayrı sekme
 * açmak JWT'yi taşımaz, ayrı pencere de formdaki işi böler.
 */
export default function BelgeGorseli({ sayfalar = [], sayfaAdresi, baslik = 'Belge', className = '',
  gorselClass = 'max-h-[75vh]' }) {
  const [sira, setSira] = useState(sayfalar[0]?.sira || 1);
  const [url, setUrl] = useState(null);
  const [hata, setHata] = useState(false);
  const [buyuk, setBuyuk] = useState(false);
  const aktif = sayfalar.find(s => s.sira === sira) || sayfalar[0];
  const pdf = (aktif?.content_type || '').includes('pdf');
  const anahtar = sayfalar.map(s => s.sira).join(',');

  useEffect(() => { setSira(sayfalar[0]?.sira || 1); }, [anahtar]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!sayfalar.length || !sayfaAdresi) return;
    let iptal = false, olusan = null;
    setUrl(null); setHata(false);
    api.get(sayfaAdresi(sira), { responseType: 'blob' })
      .then(r => { if (!iptal) { olusan = URL.createObjectURL(r.data); setUrl(olusan); } })
      .catch(() => { if (!iptal) setHata(true); });
    return () => { iptal = true; if (olusan) URL.revokeObjectURL(olusan); };
  }, [sira, anahtar]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!sayfalar.length) return null;
  return (
    <div className={`space-y-1.5 ${className}`} data-testid="belge-gorseli">
      <p className="text-[10px] font-semibold uppercase tracking-wide text-[#8a7e70] flex items-center gap-1">
        <FileText size={11} /> {baslik}{sayfalar.length > 1 ? ` · ${sayfalar.length} sayfa` : ''}
      </p>
      <div className="relative rounded-lg border border-[#d0c4b0] bg-white overflow-hidden min-h-[160px] flex items-center justify-center">
        {hata ? (
          <p className="p-4 text-xs text-[#8a7e70] text-center">Görsel yüklenemedi</p>
        ) : !url ? (
          <Loader2 className="animate-spin text-[#8a7e70]" size={20} />
        ) : pdf ? (
          <iframe src={url} title={baslik} className="w-full h-[70vh]" />
        ) : (
          <button type="button" onClick={() => setBuyuk(true)} className="block w-full cursor-zoom-in group"
            title="Büyütmek için tıklayın">
            <img src={url} alt={baslik} className={`w-full object-contain ${gorselClass}`} />
            <span className="absolute bottom-1.5 right-1.5 bg-black/55 text-white rounded px-1.5 py-0.5 text-[10px] flex items-center gap-1 opacity-80 group-hover:opacity-100">
              <ZoomIn size={11} /> Büyüt
            </span>
          </button>
        )}
      </div>
      {sayfalar.length > 1 && (
        <div className="flex items-center justify-center gap-2 text-xs text-[#6b5f4f]">
          <button type="button" disabled={sira <= sayfalar[0].sira} onClick={() => setSira(x => x - 1)}
            className="disabled:opacity-30"><ChevronLeft size={16} /></button>
          {sira} / {sayfalar.length}
          <button type="button" disabled={sira >= sayfalar[sayfalar.length - 1].sira} onClick={() => setSira(x => x + 1)}
            className="disabled:opacity-30"><ChevronRight size={16} /></button>
        </div>
      )}
      {buyuk && url && !pdf && (
        <div className="fixed inset-0 z-[60] bg-black/85 flex items-center justify-center p-4"
          onClick={() => setBuyuk(false)} data-testid="belge-gorseli-buyuk">
          <button type="button" className="absolute top-4 right-4 text-white" onClick={() => setBuyuk(false)}><X size={28} /></button>
          <img src={url} alt={baslik} className="max-h-[92vh] max-w-[94vw] object-contain"
            onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
}
